import { useEffect, useMemo, useState } from "react";
import {
  useGetOrganizationsQuery,
  useGetSalesQuery,
  useGetCustomersQuery,
} from "../../../../redux/api";
import type { Sale } from "../../../../redux/api";
import { StatCard } from "../../../../components/StatCard";
import { IconReport, IconChart, IconClock, IconCheck } from "../../../../components/icons";

const money = (v: number) => v.toLocaleString();

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : "—";

// Home → Sales. Read-only sales analytics for the selected organization.
const SalesDashboard = () => {
  const { data: organizations = [] } = useGetOrganizationsQuery();
  const [orgId, setOrgId] = useState<string>("");

  useEffect(() => {
    if (!orgId && organizations.length) setOrgId(organizations[0].id);
  }, [organizations, orgId]);

  const skip = { skip: !orgId };
  const { data: sales = [] } = useGetSalesQuery(orgId || undefined, skip);
  const { data: customers = [] } = useGetCustomersQuery(orgId || undefined, skip);

  const customerName = (id?: string | null) =>
    id ? customers.find((c) => c.id === id)?.name ?? id : "—";

  const stats = useMemo(() => {
    const value = sales.reduce((s, x) => s + (x.total ?? 0), 0);
    const delivered = sales.filter(
      (s) => (s.status ?? "").toLowerCase() === "delivered",
    ).length;
    const open = sales.filter((s) =>
      ["draft", "confirmed", "shipped"].includes((s.status ?? "").toLowerCase()),
    ).length;
    const avg = sales.length ? value / sales.length : 0;
    return { value, delivered, open, avg };
  }, [sales]);

  // Top customers by sales value.
  const topCustomers = useMemo(() => {
    const totals = new Map<string, number>();
    sales.forEach((s) => {
      if (!s.customerId) return;
      totals.set(s.customerId, (totals.get(s.customerId) ?? 0) + (s.total ?? 0));
    });
    return [...totals.entries()]
      .map(([id, total]) => ({ id, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [sales]);

  const recent = useMemo(
    () =>
      [...sales]
        .sort((a, b) => (b.saleDate ?? "").localeCompare(a.saleDate ?? ""))
        .slice(0, 8),
    [sales],
  );

  return (
    <div className="dashboard">
      <div className="dashboard__head">
        <div>
          <h1>Sales Dashboard</h1>
          <p>Sales performance for the selected organization.</p>
        </div>
        <select
          className="select"
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
        >
          {organizations.length === 0 ? (
            <option value="">No organizations</option>
          ) : (
            organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))
          )}
        </select>
      </div>

      {!orgId ? (
        <section className="panel">
          <div className="profile-empty">
            Select an organization to view its sales.
          </div>
        </section>
      ) : (
        <>
          <div className="stat-grid">
            <StatCard
              label="Total sales"
              value={sales.length}
              tone="primary"
              icon={<IconReport />}
            />
            <StatCard
              label="Sales value"
              value={money(stats.value)}
              tone="success"
              icon={<IconChart />}
            />
            <StatCard
              label="Open"
              value={stats.open}
              hint="Draft / confirmed / shipped"
              tone="warning"
              icon={<IconClock />}
            />
            <StatCard
              label="Delivered"
              value={stats.delivered}
              hint={`Avg ${money(Math.round(stats.avg))}`}
              tone="success"
              icon={<IconCheck />}
            />
          </div>

          <section className="panel">
            <div className="panel__head">
              <div>
                <h2>Recent sales</h2>
                <p className="panel__sub">Latest {recent.length}</p>
              </div>
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Sale #</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Total</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.length === 0 ? (
                    <tr>
                      <td className="data-table__empty" colSpan={5}>
                        No sales yet.
                      </td>
                    </tr>
                  ) : (
                    recent.map((s: Sale) => (
                      <tr key={s.id}>
                        <td className="cell-title">{s.saleNumber}</td>
                        <td>{customerName(s.customerId)}</td>
                        <td className="cell-date">{formatDate(s.saleDate)}</td>
                        <td className="cell-date">{money(s.total ?? 0)}</td>
                        <td>
                          <span className="pill pill--neutral">{s.status}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel">
            <div className="panel__head">
              <div>
                <h2>Top customers</h2>
                <p className="panel__sub">By sales value</p>
              </div>
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Sales value</th>
                  </tr>
                </thead>
                <tbody>
                  {topCustomers.length === 0 ? (
                    <tr>
                      <td className="data-table__empty" colSpan={2}>
                        No customer sales yet.
                      </td>
                    </tr>
                  ) : (
                    topCustomers.map((c) => (
                      <tr key={c.id}>
                        <td className="cell-title">{customerName(c.id)}</td>
                        <td className="cell-date">{money(c.total)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default SalesDashboard;
